import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from "moment";
import api from '../../../../services/api';
import { DefaultColumnFilter, DateColumnFilter } from '../../../react-table/filters';
import { useTable, useFlexLayout, useFilters, useSortBy } from 'react-table';
import Grid from '../../../Grid';
import Loader from '../../../loaders';
import { TooltipButton } from '../../../inputs';
import { PopoverBody, PopoverHeader, Table, UncontrolledPopover } from 'reactstrap';

export default function DispatchHistory(props) {
    const {
        itemInEdit,
    } = props;
    const skipPageResetRef = useRef();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const defaultSort = useMemo(() => [],[]);
    const [sortBy, setSortBy] = useState(defaultSort);
    const [candidateId, setCandidateId] = useState(0);
    const [flights, setFlights] = useState([]);

    const refreshData = useCallback(() => {
        setData([]);

        if (!candidateId || !itemInEdit?.dispatchHistory)
            return;

        setLoading(true);
        skipPageResetRef.current = true;

        const apiCalls = [
            api.get(`candidate/flights/${candidateId}`),
        ]

        Promise.all(apiCalls)
            .then(responses => {
                const newData = JSON.parse(JSON.stringify(itemInEdit?.dispatchHistory ?? []));
                const newFlights = responses[0].data?.flights.map(f => ({
                    ...f,
                    taken: false,
                }));

                if (newData.length)
                    newData.forEach(d => {
                        const currentSlotFlights = newFlights.filter(f => f.rosterCandidateId === d.rosterCandidateId && !f.taken);
                        d.flights = [];

                        if (!currentSlotFlights?.length || !d.nullableStartDate || !d.nullableEndDate)
                            return;

                        const strStartDate = new Date(moment(d.nullableStartDate).format("YYYY-MM-DD"));
                        const strEndDate = new Date(moment(d.nullableEndDate).format("YYYY-MM-DD"));

                        currentSlotFlights.sort((a, b) => {
                            const aDepartureDate = new Date(moment(a.departureFlightDateTime).format("YYYY-MM-DD"));
                            const bDepartureDate = new Date(moment(b.departureFlightDateTime).format("YYYY-MM-DD"));
                            const compareResult = Math.abs(aDepartureDate - strStartDate) - Math.abs(bDepartureDate - strStartDate);
                            return compareResult;
                        });
                        currentSlotFlights[0].taken = true;
                        const mobeFlight = JSON.parse(JSON.stringify(currentSlotFlights[0]));
                        d.flights.push(mobeFlight);

                        currentSlotFlights.sort((a, b) => {
                            const aDepartureDate = new Date(moment(a.departureFlightDateTime).format("YYYY-MM-DD"));
                            const bDepartureDate = new Date(moment(b.departureFlightDateTime).format("YYYY-MM-DD"));
                            const compareResult = Math.abs(aDepartureDate - strEndDate) - Math.abs(bDepartureDate - strEndDate);
                            return compareResult;
                        });
                        currentSlotFlights[0].taken = true;
                        const demobeFlight = JSON.parse(JSON.stringify(currentSlotFlights[0]));
                        d.flights.push(demobeFlight);
                    });

                setData(newData);
                setFlights(newFlights);
            })
            .catch(error => console.error({error}))
            .finally(() => setLoading(false))
            ;
    }, [candidateId, itemInEdit]);

    useEffect(() => {
        if (candidateId === (itemInEdit?.candidateId ?? 0) || !itemInEdit?.dispatchHistory)
            return;

        setCandidateId(itemInEdit?.candidateId ?? 0);
    }, [itemInEdit?.dispatchHistory]);
    
    useEffect(() => {
        refreshData();
    }, [candidateId]);

    const columns = useMemo(() => [
        {
            id: 'expander',
            Header: () => null,
            Cell: ({ row }) => {
                const { original } = row;
                const id = `expand_button_${original.rosterCandidateId}_${row.id}`;

                return original.flights?.length
                    ?
                    (
                        <div className="w-100 d-flex flex-wrap justify-content-center align-items-center">
                            <TooltipButton
                                id={id}
                                title="Show Flights"
                                className="btn-icon"
                                color="default"
                                size="xsm"
                                type="button"
                            >
                                <i className="fas fa-plane-departure" />
                            </TooltipButton>
                            <UncontrolledPopover
                                placement="bottom"
                                target={`${id}`}
                                id={`${id}_popover`}
                                trigger="legacy"
                                className="full-width"
                            >
                                <PopoverHeader>
                                    Flight Info
                                </PopoverHeader>
                                <PopoverBody>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>
                                                    Flight Date
                                                </th>
                                                <th>
                                                    Time
                                                </th>
                                                <th>
                                                    Flight Type
                                                </th>
                                                <th>
                                                    Status
                                                </th>
                                                <th>
                                                    From
                                                </th>
                                                <th>
                                                    To
                                                </th>
                                                <th>
                                                    Flight Number
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {
                                            original.flights.map(f => {
                                                return (
                                                    <tr>
                                                        <td>{f.departureFlightDateTime ? moment(f.departureFlightDateTime.slice(0, 10)).format(`dddd, DD MMM YYYY`) : null}</td>
                                                        <td>{f.departureFlightDateTime ? f.departureFlightDateTime.slice(11,16) : null}</td>
                                                        <td>{f.rosterFlightType}</td>
                                                        <td>{f.status}</td>
                                                        <td>{`${f.departureAirport} (Terminal ${f.departureAirportTerminal})`}</td>
                                                        <td>{`${f.arrivalAirport} (Terminal ${f.arrivalAirportTerminal})`}</td>
                                                        <td>{f.flightNumber}</td>
                                                    </tr>
                                                );
                                            })
                                        }
                                        </tbody>
                                    </Table>
                                </PopoverBody>
                            </UncontrolledPopover>
                        </div>
                    )
                    :
                    null
                ;
            },
            width: 40,
        },
        {
            id: "projectName",
            Header: "Project",
            accessor: (row) => row.projectName,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 100,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "dispatchUnitName",
            Header: "Dispatch Unit",
            accessor: (row) => row.dispatchUnitName,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 100,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "hrispositionMasterName",
            Header: "Roster Position",
            accessor: (row) => row.hrispositionMasterName,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 135,
            //width: 130,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "positionName",
            Header: "Employed Position",
            accessor: (row) => row.positionName,
            Filter: DefaultColumnFilter,
            filter: "text",
            //width: 100,
            width: 125,
            //width: 150,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "shiftName",
            Header: "Shift",
            accessor: (row) => row.shiftName,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 60,
            //width: 70,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "confirmationStatus",
            Header: "Confirmation",
            accessor: (row) => row.status,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 110,
            //width: 170,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "entity",
            Header: "Entity",
            accessor: (row) => row.companyName,
            Filter: DefaultColumnFilter,
            filter: "text",
            width: 100,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "mobeDate",
            Header: "Mobe Date",
            Cell: ({ row }) => {
                const { original } = row;
                return original.nullableStartDate ? moment(original.nullableStartDate, "YYYY-MM-DD").format(`dd, DD.MM.YYYY`) : "-";
            },
            accessor: (row) => row.nullableStartDate ? moment(row.nullableStartDate, "YYYY-MM-DD").format(`DD.MM.YYYY`) : "-",
            Filter: (e) => DateColumnFilter({ ...e, dateFormat: "DD.MM.YYYY", dateMask: "99.99.9999" }),
            filter: "text",
            width: 100,
            //width: 150,
            disableSortBy: false,
            disableFilters: false,
        },
        {
            id: "demobeDate",
            Header: "Demobe Date",
            Cell: ({ row }) => {
                const { original } = row;
                return original.nullableEndDate ? moment(original.nullableEndDate, "YYYY-MM-DD").format(`dd, DD.MM.YYYY`) : "-";
            },
            accessor: (row) => row.nullableEndDate ? moment(row.nullableEndDate, "YYYY-MM-DD").format(`DD.MM.YYYY`) : "-",
            Filter: (e) => DateColumnFilter({ ...e, dateFormat: "DD.MM.YYYY", dateMask: "99.99.9999" }),
            filter: "text",
            width: 100,
            //width: 150,
            disableSortBy: false,
            disableFilters: false,
        },
    ], [data, flights]);

    const defaultColumn = React.useMemo(() => ({
        width: 150,
        Filter: DefaultColumnFilter,
        filter: "text",
        disableSortBy: false,
        disableFilters: false,
    }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
    } = useTable(
        {
            columns,
            data,
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetRowState: !skipPageResetRef.current,
            defaultColumn,
        },
        useFlexLayout,
        useFilters,
        useSortBy,
    );

    React.useEffect(() => {
        setFilters(state.filters);
    }, [state.filters]);

    React.useEffect(() => {
        setSortBy(state.sortBy);
    }, [state.sortBy]);

    return (
        <>
            {loading && <Loader />}
            <Grid
                rows={rows}
                tableProps={getTableProps()}
                headerGroups={headerGroups}
                tableBodyProps={getTableBodyProps()}
                prepareRow={prepareRow}
                height={'calc(42vh)'}
            />
        </>
    );
}